/**
 * **导入问题列表**（行号 + 词元 + 原文 + 原因）—— 两个导入界面**共用一份**。
 *
 * ## 为什么单独抽出来
 *
 * 「按书录入」与「整块导入（高级）」各抄了一份几乎一样的列表，而本轮要在这里加
 * "可折叠"（一次粘 300 行时不许把手机页面撑爆）—— 两份各改一遍迟早漂移，
 * 而漂移的那一份就是家长看不到具体原因的那一份。
 *
 * ## 为什么必须显示**行号 + 词元 + 原文**
 *
 * 用户实测（2026-10-16）的真实感受是"报错太笼统，看不出哪一行、哪个字有问题"。
 * 家长粘的是几十上百行，只说"有内容不对"等于没给信息。所以每一条都要能回答：
 *
 *   1. **第几行**（`line <= 0` / 没有行号的是"整份内容"级的原因）；
 *   2. **哪个词元**（`天地` —— 一行里可能粘了 20 个字元）；
 *   3. **原文长什么样**（灰色等宽小字，能一眼对上自己粘的那一行）；
 *   4. **为什么不行**。
 *
 * ## 折叠与滚动
 *
 *   · 列表本身 `overflow-y-auto` + `max-h`：**再长也不会把页面撑爆**；
 *   · 超过 `ISSUE_COLLAPSE_THRESHOLD` 条时只先渲染前几条 + 一个「展开全部」按钮：
 *     手机上先看到"最要命的头几条"，需要时再展开。
 *
 * ⚠️ 入参类型 `ImportIssueRow` 在 `lib/importIssues.ts`（**纯逻辑层**）——
 *    状态机与接口层都只 import 那个类型，不 import 本组件。
 */
import { useState } from 'react';
import { IconAlertTriangle } from '../ui/Icons';
import { cn } from '../../lib/utils';
import type { ImportIssueRow } from '../../lib/importIssues';

/** 超过这么多条就**先折起来**（一次粘 300 行时的默认状态）。 */
export const ISSUE_COLLAPSE_THRESHOLD = 8;

export function ImportIssueList({
  title,
  issues,
  tone,
}: {
  title: string;
  /**
   * 要显示的问题。
   *
   * ⚠️ 允许 `null`（"这份列表没拿到"）：后端响应里 `errors` / `warnings`
   *    拿不到时是 `null`，那与"有 0 条问题"**不是一回事** —— 但在这里两者的
   *    渲染结果都是"不显示"，所以由调用方决定要不要另外说一句。
   */
  issues: readonly ImportIssueRow[] | null | undefined;
  tone: 'error' | 'warn';
}) {
  const [expanded, setExpanded] = useState(false);

  const rows = issues ?? [];
  if (rows.length === 0) return null;

  const collapsible = rows.length > ISSUE_COLLAPSE_THRESHOLD;
  const shown = collapsible && !expanded ? rows.slice(0, ISSUE_COLLAPSE_THRESHOLD) : rows;

  return (
    <div className="min-w-0">
      <p
        className={cn(
          'mb-1 flex items-center gap-1 text-sm font-semibold',
          tone === 'error' ? 'text-rose-700' : 'text-amber-700',
        )}
      >
        <IconAlertTriangle size={16} />
        {title}（{rows.length}）
      </p>
      <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
        {shown.map((issue, index) => (
          <li
            key={`${issue.line ?? 0}-${index}-${issue.token ?? ''}`}
            className={cn(
              'rounded-xl border px-3 py-2 text-sm',
              tone === 'error'
                ? 'border-rose-200 bg-rose-50 text-rose-800'
                : 'border-amber-200 bg-amber-50 text-amber-800',
            )}
          >
            <span className="mr-1 shrink-0 font-semibold tabular-nums">
              {(issue.line ?? 0) > 0 ? `第 ${issue.line} 行` : '整份内容'}
            </span>
            {issue.token !== null && issue.token !== undefined && issue.token !== '' && (
              <span
                className={cn(
                  'mr-1 shrink-0 rounded px-1 font-mono text-xs font-semibold',
                  tone === 'error'
                    ? 'bg-rose-200/70 text-rose-900'
                    : 'bg-amber-200/70 text-amber-900',
                )}
              >
                {issue.token}
              </span>
            )}
            <span>{issue.message}</span>
            {issue.raw !== undefined && issue.raw !== '' && (
              <span className="mt-1 block truncate rounded-lg bg-white/70 px-2 py-1 font-mono text-xs text-slate-600">
                {issue.raw}
              </span>
            )}
          </li>
        ))}
      </ul>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className={cn(
            'mt-1.5 rounded-lg border px-2 py-1 text-xs font-semibold',
            tone === 'error'
              ? 'border-rose-200 bg-white/70 text-rose-700'
              : 'border-amber-200 bg-white/70 text-amber-700',
          )}
        >
          {expanded ? '收起' : `展开全部 ${rows.length} 条`}
        </button>
      )}
    </div>
  );
}
